import React, { useState, useEffect } from "react";
import "./App.css";
import config from "./config";
import Navbar from "./Navbar";
import { IncidentExpandSVG } from "./SVGs.js";

function App() {
  const API_URL = config.API_URL;
  const serviceId = config.serviceId;
  const serviceLogoURL = config.serviceLogoURL;
  const [serviceStatus, setServiceStatus] = useState(null);
  const [serviceIncidents, setServiceIncidents] = useState([]);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signupMessage, setSignupMessage] = useState(null);

  useEffect(() => {
    // Fetch service status
    fetch(`${API_URL}/GetServiceStatus/${serviceId}`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Error: ${response.statusText}`);
        }
        return response.json();
      })
      .then((data) => {
        setServiceStatus(data);
        setLoading(false);
      })
      .catch((fetchError) => {
        setError(fetchError.message);
        setLoading(false);
      });

    // Fetch service incidents
    fetch(`${API_URL}/GetServiceIncidents/${serviceId}`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Error: ${response.statusText}`);
        }
        return response.json();
      })
      .then((data) => {
        setServiceIncidents(data);
        setLoading(false);
      })
      .catch((fetchError) => {
        setError(fetchError.message);
        setLoading(false);
      });
  }, [API_URL, serviceId]);

  const getStatusStyle = (status) => {
    switch (status) {
      case "Operational":
        return { color: "#188038", backgroundColor: "rgba(52, 168, 83, 0.14)" };
      case "Active incident":
        return { color: "#b06000", backgroundColor: "rgba(251, 188, 5, 0.18)" };
      case "Outage":
        return { color: "#c5221f", backgroundColor: "rgba(234, 67, 53, 0.14)" };
      default:
        return {};
    }
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
  };

  const handleSignup = () => {
    // Validate email
    if (!email) {
      alert("Please enter a valid email address.");
      return;
    }

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(email)) {
      alert("Please enter a valid email address.");
      return;
    }

    // Call the AlertEmailSignup API endpoint
    fetch(`${API_URL}/AlertEmailSignup`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email, serviceId }),
    })
      .then((response) => {
        if (response.status === 409) {
          // Email already subscribed
          alert("This email is already subscribed.");
          throw new Error("Email already subscribed");
        } else if (!response.ok) {
          throw new Error(`Error: ${response.statusText}`);
        }
        return response.json();
      })
      .then((data) => {
        setSignupMessage(data.message);
        setEmail("");
      })
      .catch((signupError) => {
        if (signupError.message !== "Email already subscribed") {
          setSignupMessage("An error occurred while signing up. Please try again.");
        }
      });
  };

  const sortedServiceIncidents = [...serviceIncidents].sort((a, b) => new Date(b.PublishDate) - new Date(a.PublishDate));
  const currentStatus = serviceStatus?.CurrentStatus || "Unknown";

  return (
    <div className="App">
      <Navbar />

      <main className="App-main">
        <header className="App-header">
          {serviceStatus && (
            <section className="App-hero-card">
              <div className="App-hero-copy">
                <div className="App-hero-top-row">
                  <div className="App-hero-text">
                    <p className="App-eyebrow">Service status monitor</p>
                    <h1 className="App-headline-title">Is {serviceStatus.DisplayName} Down?</h1>
                  </div>
                  <img className="App-service-logo App-service-logo-hero" src={serviceLogoURL} alt="Service Logo" />
                </div>
                <p className="App-hero-description">
                  Live status and recent incident history for {serviceStatus.DisplayName}, with optional email alerts when things change.
                </p>
              </div>

              <div className="App-status-card">
                <div className="App-status-meta">
                  <p className="App-status-label">Current status</p>
                  <div className="App-status-row">
                    <h2 className="App-current-status-display-name">{serviceStatus.DisplayName}</h2>
                    <span className="App-status-pill" style={getStatusStyle(currentStatus)}>
                      {currentStatus}
                    </span>
                  </div>
                </div>
              </div>

              {loading && <p className="App-feedback-message">Loading...</p>}
              {error && <p className="App-feedback-message App-feedback-error">Error: {error}</p>}

              <section className="App-signup-card">
                <div className="App-signup-copy">
                  <h3 className="App-signup-label">Get alerts for incidents and outages</h3>
                  <p className="App-signup-subtitle">
                    Subscribe with your email to hear about new issues affecting {serviceStatus.DisplayName}.
                  </p>
                  <div className="App-signup-powered-by">
                    <span className="App-powered-by-inline">Powered by</span>
                    <img className="App-alerts-logo App-alerts-logo-inline" src="https://www.signaltuner.com/images/SignalTUNER%20Logo%20Horizontal-01.png" alt="SignalTuner Logo" />
                  </div>
                </div>

                <div className="App-email-container">
                  <input className="App-email-input" type="email" value={email} onChange={handleEmailChange} placeholder="Enter your email" />
                  <button className="App-email-submit-button" onClick={handleSignup}>
                    Submit
                  </button>
                </div>

                {signupMessage && <p className="App-feedback-message">{signupMessage}</p>}
              </section>

              {/* <div className="App-alerts-container" onClick={() => window.open("https://signaltuner.com", "_blank", "noopener,noreferrer")}>
                <div className="App-signup-logo-container">
                  <p className="App-powered-by">Alerts provided by</p>
                  <img className="App-alerts-logo" src="https://www.signaltuner.com/images/SignalTUNER%20Logo%20Horizontal-01.png" alt="SignalTuner Logo" />
                </div>
                <div className="App-alerts-copy">
                  <h4 className="App-question-content">Need help diagnosing a problem?</h4>
                  <p className="App-question-supporting-copy">
                    SignalTuner combines service status data with device monitoring to identify connectivity issues faster.
                  </p>
                </div>
                <h3 className="App-alerts-cta">Try it free</h3>
              </div> */}
            </section>
          )}

          <section className="App-recent-incidents-div">
            <div className="App-section-heading">
              <h2 className="App-recent-incidents-label">Recent incidents</h2>
              <p className="App-section-subtitle">The latest published incidents, with active items pinned to the top.</p>
            </div>

            {sortedServiceIncidents.length > 0 && (
              <div className="App-incidents-list">
                {sortedServiceIncidents
                  .sort((a, b) => {
                    if (a.Status === "Active" && b.Status !== "Active") return -1;
                    if (a.Status !== "Active" && b.Status === "Active") return 1;
                    return 0;
                  })
                  .slice(0, 50)
                  .map((incident) => (
                    <ExpandableIncident key={incident.IncidentId} incident={incident} serviceStatus={serviceStatus} />
                  ))}
              </div>
            )}

            {sortedServiceIncidents.length === 0 && !loading && <p className="App-empty-state">No recent incidents reported.</p>}
          </section>
        </header>
      </main>
    </div>
  );
}

const ExpandableIncident = ({ incident, serviceStatus }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className="App-incident-div" onClick={toggleExpand}>
      <div className="App-incident-header">
        <div className="App-incident-title-block">
          <h3 className="App-incident-title">{incident.Title}</h3>
          <p className="App-incident-status-line">
            Status
            <span className="App-incident-status-pill" data-active={incident.Status === "Active"}>
              {incident.Status}
            </span>
          </p>
        </div>
        <IncidentExpandSVG isExpanded={isExpanded} />
      </div>

      {isExpanded && (
        <div className="App-incident-content">
          <div className="App-incident-description" dangerouslySetInnerHTML={{ __html: incident.Description }} />
          <h4 className="App-incident-link">
            <a href={incident.Link} target="_blank" rel="noopener noreferrer">
              {serviceStatus ? serviceStatus.DisplayName : "Service"} incident report
            </a>
          </h4>
          <p className="App-incident-date">Published on: {new Date(incident.PublishDate).toLocaleDateString()}</p>
        </div>
      )}
    </div>
  );
};

export default App;
